import type { KyInstance, KyResponse, Options } from "ky";

/**
 * AgentCore runtime-session pinning.
 *
 * AgentCore routes an `/invocations` request to a microVM by its
 * `X-Amzn-Bedrock-AgentCore-Runtime-Session-Id` header. Without one, every request gets a fresh
 * session — a fresh microVM and a cold start, which is most of the Ask Tiger first-turn latency.
 * Sending the same value on the warm-up sent when the panel opens and on every turn of one chat
 * keeps the agent's process, and its caches, warm.
 *
 * The value is derived from the chat's `session_id` only, and AgentCore itself does not tie a
 * runtime session to a user. So turns carry it only once the session's warm-up probe shows the
 * server refuses a runtime session to anyone but its first user (see `canPinTurns` in
 * `warmup.ts`); until then they go unpinned. The probe itself is always pinned — it is what boots
 * the microVM — and is harmless on any server version.
 *
 * Mirrors agent-delivery-fx `core/runtime_session.py` — the two share test vectors, so change them
 * together.
 */

/** Header AgentCore reads to pick the microVM. Allow-listed by the gateway's CloudFront CORS policy. */
export const RUNTIME_SESSION_HEADER = "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id";

// InvokeAgentRuntime takes 33-256 characters on the way in, but echoes the id back in a response
// header documented as at most 100 characters of this pattern, so stay inside the narrower contract.
const MIN_LENGTH = 33;
const MAX_LENGTH = 100;
const VALID = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;
const ENCODED_PREFIX = "ts-";

function utf8Hex(value: string): string {
  return Array.from(new TextEncoder().encode(value), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

/**
 * The runtime-session header value for `sessionId`, or undefined to send no header.
 *
 * Deterministic: an id that already fits AgentCore's contract (a UUID does) is used as is; anything
 * else is hex-encoded behind `ts-` and right-padded with `-` to the minimum length (hex never
 * contains `-`, so padding can't make two ids collide); an id too long to encode within 100
 * characters is not pinned.
 */
export function runtimeSessionId(sessionId: string | undefined | null): string | undefined {
  if (!sessionId) return undefined;
  if (sessionId.length >= MIN_LENGTH && sessionId.length <= MAX_LENGTH && VALID.test(sessionId)) {
    return sessionId;
  }
  const encoded = ENCODED_PREFIX + utf8Hex(sessionId);
  if (encoded.length > MAX_LENGTH) return undefined;
  return encoded.padEnd(MIN_LENGTH, "-");
}

/** Request headers that pin a call to `sessionId`'s microVM (empty when it can't be pinned). */
export function runtimeSessionHeaders(
  sessionId: string | undefined | null,
): Record<string, string> {
  const value = runtimeSessionId(sessionId);
  return value ? { [RUNTIME_SESSION_HEADER]: value } : {};
}

/** Attempts after the first for AgentCore's 409 (300ms, 600ms, 1.2s, 2s apart). */
const CONFLICT_RETRIES = 4;
const CONFLICT_BASE_DELAY_MS = 300;
const CONFLICT_MAX_DELAY_MS = 2000;

function isConflict(error: unknown): boolean {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return status === 409;
}

function wait(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true },
    );
  });
}

/**
 * `api.post` that retries only AgentCore's 409 RetryableConflictException, which it returns while
 * it is still provisioning the session's microVM — routine once the panel-open warm-up has just
 * started it and the user clicks a card a moment later. The request never reached the agent, so a
 * retry cannot repeat a turn; every other failure (and an aborted turn) surfaces on the first
 * attempt.
 *
 * Deliberately not ky's own `retry` for POST: that re-sends a cloned, stream-backed Request, whose
 * body devtools and Playwright can no longer read. Each attempt here is a fresh `post` with the
 * same plain JSON body.
 */
export async function postWithConflictRetry(
  api: KyInstance,
  path: string,
  options: Options,
): Promise<KyResponse> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await api.post(path, { ...options, retry: 0 });
    } catch (error) {
      if (attempt >= CONFLICT_RETRIES || !isConflict(error) || options.signal?.aborted) throw error;
      await wait(
        Math.min(CONFLICT_BASE_DELAY_MS * 2 ** attempt, CONFLICT_MAX_DELAY_MS),
        options.signal,
      );
    }
  }
}
